import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Calendar,
  CheckCircle2,
  ClipboardList,
  FileText,
  FlaskConical,
  FlaskRound,
  Search,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard } from '../../components/shared/stat-card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FilterPills } from '../../components/ui/filter-pills';
import { usePermissions } from '../../hooks/use-permissions';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { MockTahseelRequestModal } from './MockTahseelRequestModal';

// ── Status config ──────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'primary' | 'success' | 'error' | 'warning';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  dot: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  NEW: { label: 'New', variant: 'default', dot: 'bg-gray-400' },
  ASSIGNED: { label: 'Assigned', variant: 'primary', dot: 'bg-brand-400' },
  SITE_VISIT_SCHEDULED: { label: 'Site Visit', variant: 'warning', dot: 'bg-warning-400' },
  REQUEST_LAB_TEST: { label: 'Lab Test', variant: 'warning', dot: 'bg-warning-500' },
  LAB_SITE_VISIT: { label: 'Lab Site Visit', variant: 'warning', dot: 'bg-warning-500' },
  PREPARE_STUDY_REPORT: {
    label: 'Preparing Study Report',
    variant: 'warning',
    dot: 'bg-warning-500',
  },
  STUDY_REPORT_SM_APPROVAL: {
    label: 'Waiting for Approval',
    variant: 'primary',
    dot: 'bg-brand-500',
  },
  FINAL_REPORT_ISSUED: { label: 'Report Issued', variant: 'success', dot: 'bg-success-500' },
  SENT_TO_SAP: { label: 'SAP Synced', variant: 'success', dot: 'bg-success-500' },
  REJECTED: { label: 'Rejected', variant: 'error', dot: 'bg-error-500' },
};

const FOLDER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'UNDER_REVIEW', label: 'Waiting for Approval' },
  { value: 'IN_PROGRESS_WITH_LAB', label: 'In Progress with Lab' },
  { value: 'COMPLETED_STUDIES', label: 'Completed Studies' },
];

const searchSchema = z.object({
  folder: z.string().optional(),
  status: z.string().optional(),
  assignedConsultantId: z.string().optional(),
  authorityAssigneeId: z.string().optional(),
  unassignedAuthorityOnly: z.string().optional(),
  search: z.string().optional(),
  page: z.number().catch(1),
  limit: z.number().catch(20),
});

// ── Types ──────────────────────────────────────────────────────────────────────

interface StudyRequestListItem {
  id: string;
  referenceNo: string;
  status: string;
  createdAt: string;
  reportSubmissionDate: string | null;
  studyType: { id: string; name: string; code: string } | null;
  account: { id: string; name: string };
  contact: { id: string; fullName: string; email: string };
  assignedConsultant: { id: string; name: string; email: string } | null;
  authorityAssignee: { id: string; name: string; email: string } | null;
}

interface StudyRequestsResponse {
  items: StudyRequestListItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const Route = createFileRoute('/study-requests/')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: () => {
    const { user, permissions, licensingAuthorities } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-study:view')) throw redirect({ to: '/dashboard' });
    if (licensingAuthorities.length > 0 && !licensingAuthorities.includes('SHARJAH_MUNICIPALITY')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: StudyRequestsPage,
});

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <Badge variant="default">{status}</Badge>;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap',
        cfg.variant === 'default' && 'border-gray-200 bg-gray-50 text-gray-600',
        cfg.variant === 'primary' && 'border-brand-200 bg-brand-50 text-brand-600',
        cfg.variant === 'success' && 'border-success-200 bg-success-50 text-success-600',
        cfg.variant === 'warning' && 'border-warning-200 bg-warning-50 text-warning-600',
        cfg.variant === 'error' && 'border-error-200 bg-error-50 text-error-600',
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

// ── Stat helpers ───────────────────────────────────────────────────────────────

function useStudyCountStat(key: string, params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['study-requests', 'stat', key],
    queryFn: () =>
      api
        .get('study-requests', { searchParams: { ...params, page: '1', limit: '1' } })
        .json<StudyRequestsResponse>(),
    staleTime: 60_000,
    select: (d) => d.meta.total,
  });
}

// ── Columns ────────────────────────────────────────────────────────────────────

const STUDY_COLUMNS: CardDataTableColumn[] = [
  { id: 'request', header: 'Study Request' },
  { id: 'account', header: 'Account' },
  { id: 'status', header: 'Status' },
  { id: 'assignee', header: 'Assignee' },
  { id: 'reportDate', header: 'Report Due' },
  { id: 'createdAt', header: 'Created' },
];

const STUDY_GRID = 'minmax(180px, 1.5fr) minmax(150px, 1.2fr) 150px minmax(130px, 1fr) 110px 90px';

// ── Page ───────────────────────────────────────────────────────────────────────

function StudyRequestsPage() {
  const navigate = useNavigate({ from: '/study-requests/' });
  const search = Route.useSearch();
  const { can } = usePermissions();
  const canMockTahseelRequest = can('sm-study:mock-tahseel-request');
  const [showMockModal, setShowMockModal] = useState(false);

  const [localSearch, setLocalSearch] = useState(search.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (localSearch === (search.search ?? '')) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      navigate({ search: { ...search, search: localSearch || undefined, page: 1 }, replace: true });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localSearch, search.search, navigate, search]);

  const totalStat = useStudyCountStat('total');
  const underReviewStat = useStudyCountStat('under-review', { folder: 'UNDER_REVIEW' });
  const labStat = useStudyCountStat('lab', { folder: 'IN_PROGRESS_WITH_LAB' });
  const completedStat = useStudyCountStat('completed', { folder: 'COMPLETED_STUDIES' });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['study-requests', search],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(search.page),
        limit: String(search.limit),
      };
      if (search.folder && search.folder !== '__all__') params.folder = search.folder;
      if (search.status) params.status = search.status;
      if (search.assignedConsultantId) params.assignedConsultantId = search.assignedConsultantId;
      if (search.authorityAssigneeId) params.authorityAssigneeId = search.authorityAssigneeId;
      if (search.unassignedAuthorityOnly) {
        params.unassignedAuthorityOnly = search.unassignedAuthorityOnly;
      }
      if (search.search) params.search = search.search;
      return api.get('study-requests', { searchParams: params }).json<StudyRequestsResponse>();
    },
  });

  const items = data?.items ?? [];
  const total = data?.meta.total ?? 0;

  const activeFolder = search.folder ?? '__all__';

  function setFolder(value: string) {
    navigate({
      search: { ...search, folder: value === '__all__' ? undefined : value, page: 1 },
      replace: true,
    });
  }

  return (
    <AppLayout>
      <PageHeader
        title="Study Requests"
        description="Track and manage all Sharjah Municipality environmental study requests."
        actions={
          canMockTahseelRequest ? (
            <Button variant="outline" onClick={() => setShowMockModal(true)}>
              <FlaskRound size={14} />
              Mock Tahseel Request
            </Button>
          ) : undefined
        }
      />

      {/* Stat cards */}
      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          icon={<ClipboardList size={17} />}
          iconBg="bg-brand-50"
          iconColor="text-brand-500"
          label="Total Studies"
          value={totalStat.data}
          loading={totalStat.isLoading}
        />
        <StatCard
          icon={<FileText size={17} />}
          iconBg="bg-purple-50"
          iconColor="text-purple-500"
          label="Under Review"
          value={underReviewStat.data}
          loading={underReviewStat.isLoading}
        />
        <StatCard
          icon={<FlaskConical size={17} />}
          iconBg="bg-warning-50"
          iconColor="text-warning-500"
          label="In Progress with Lab"
          value={labStat.data}
          loading={labStat.isLoading}
        />
        <StatCard
          icon={<CheckCircle2 size={17} />}
          iconBg="bg-success-50"
          iconColor="text-success-500"
          label="Completed Studies"
          value={completedStat.data}
          loading={completedStat.isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-64 flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Search by reference, account name, or TRN…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm placeholder:text-gray-400 transition-all focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <FilterPills options={FOLDER_PILLS} value={activeFolder} onChange={setFolder} />

        <span className="ml-auto text-xs text-gray-400">
          {total} {total === 1 ? 'study request' : 'study requests'}
        </span>
      </div>

      {/* Table */}
      <CardDataTable
        gridTemplateColumns={STUDY_GRID}
        columns={STUDY_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load study requests</p>
              <p className="mt-1 text-xs text-error-500">Please refresh the page</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <ClipboardList size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No study requests found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your filters or search terms</p>
          </div>
        }
        rows={items.map((item): CardDataTableRowDef => {
          const assignee = item.assignedConsultant ?? item.authorityAssignee;
          return {
            id: item.id,
            avatar: (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400 transition-colors group-hover:bg-brand-50 group-hover:text-brand-500">
                <ClipboardList size={18} />
              </div>
            ),
            cells: {
              request: (
                <div className="min-w-0">
                  <p className="font-mono text-base font-bold tracking-tight text-brand-600 transition-colors group-hover:text-brand-700">
                    {item.referenceNo}
                  </p>
                  <p className="mt-0.5 truncate text-xs font-medium text-gray-500 leading-snug">
                    {item.studyType?.name ?? '—'}
                  </p>
                </div>
              ),
              account: (
                <div className="flex items-center gap-2.5 min-w-0">
                  <GradientAvatar name={item.account.name} size="sm" shape="rounded" />
                  <p className="truncate text-sm font-medium text-gray-800">{item.account.name}</p>
                </div>
              ),
              status: <StatusBadge status={item.status} />,
              assignee: assignee ? (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100">
                    <User size={11} className="text-gray-500" />
                  </div>
                  <p className="truncate text-xs text-gray-700">{assignee.name}</p>
                </div>
              ) : (
                <span className="text-xs italic text-gray-400">Unassigned</span>
              ),
              reportDate: (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  {item.reportSubmissionDate ? (
                    <>
                      <Calendar size={11} className="shrink-0 text-gray-400" />
                      {formatDate(item.reportSubmissionDate)}
                    </>
                  ) : (
                    <span className="italic">—</span>
                  )}
                </div>
              ),
              createdAt: (
                <span className="text-xs text-gray-400">{formatDate(item.createdAt)}</span>
              ),
            },
            onClick: () =>
              navigate({
                to: '/study-requests/$requestId',
                params: { requestId: item.id },
              }),
            actions: [],
            expandContent: (
              <p className="text-xs text-gray-400">
                Contact: <span className="font-medium text-gray-600">{item.contact.fullName}</span>
                <span className="mx-1.5 text-gray-300">·</span>
                {item.contact.email}
              </p>
            ),
          };
        })}
      />

      {/* Pagination */}
      {!isLoading && total > search.limit && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Showing {items.length} of {total}
          </p>
          <div className="flex items-center gap-1">
            {Array.from(
              { length: Math.min(Math.ceil(total / search.limit), 5) },
              (_, i) => i + 1,
            ).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => navigate({ search: { ...search, page: p }, replace: true })}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors',
                  search.page === p
                    ? 'bg-brand-500 text-white shadow-sm shadow-brand-500/25'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {canMockTahseelRequest && (
        <MockTahseelRequestModal open={showMockModal} onClose={() => setShowMockModal(false)} />
      )}
    </AppLayout>
  );
}
